import getUrlParamsToObject from './getUrlParamsToObject';

/**
 * Normalize the parameters to lowercase
 * @param {Object} params - The parameters to normalize
 * @returns {Object} - The normalized parameters
 */
function normalizeParams(params) {
	return Object.fromEntries(
		Object.entries(params).map(([key, value]) => [key.toLowerCase(), value])
	) ?? {};
}

/**
 * Function to get detailed traffic source information.
 * @param {string} trafficSource - The traffic source value.
 * @param {string} url - The URL to analyze.
 * @returns {Object} - Object containing source and optional group properties.
 */
function getTrafficSourceDetails(trafficSource, url) {
	const result = {
		source: trafficSource
	};

	const params = normalizeParams(getUrlParamsToObject(url));

	// For AVM traffic, determine group type (social vs native)
	if (trafficSource === 'avm') {
		// Check raw URL string for '=facebook' pattern
		const hasFacebook = url.toLowerCase().includes('=facebook');
		const channelType = params.channel?.split('_')[1]?.toLowerCase() ?? '';
		const avmTypes = ['native', 'social', 'display'];

		// If the channel type is a valid AVM type, set the group to the channel type
		if (avmTypes.includes(channelType)) {
			result.group = channelType;
		} else {
			// If the channel type is not a valid AVM type, use the Facebook pattern to determine the group
			result.group = hasFacebook ? 'social' : 'native';
		}
	}

	return result;
}

/**
 * Determines the traffic sources based on URL parameters.
 * @param {Object} options - Options object.
 * @param {boolean} options.getDetails - Whether to return detailed object (defaults to false).
 * @returns {string|Object|null} - Traffic source string, detailed object, or null.
 */
export default function getTrafficSource(options = {}) {
	const { getDetails = false } = options;
	const url = window.location.href.replace(window.location.hash, '');

	let trafficSource = null;

	const params = getUrlParamsToObject(url);
	if (params) {
		// Normalize keys to lower case for case-insensitive matching
		const normalizedParams = normalizeParams(params);

		// These conditions are mimicked from Heap segmentation
		const ak = normalizedParams.ak?.toLowerCase() ?? '';
		const hasHsFutureAds = ak === 'qsppc_hs-futureads';
		const hasModOpenAi = ak === 'mod_openai_hs';
		const hasAff = url.includes('=Aff');
		const hasQuadlink = 'quadlink' in normalizedParams;
		const hasCCIDorCLK = 'ccid' in normalizedParams || 'clk' in normalizedParams;
		const hasAVMParam = normalizedParams.channel && normalizedParams.channel.toLowerCase().includes('avm');
		const hasHBParam = normalizedParams.channel && normalizedParams.channel.toLowerCase().includes('hb');
		const hasPubIdentifiers = hasCCIDorCLK || hasAff;
		const hasSemIdentifiers = ((url.includes('gclid') || url.includes('msclkid')) && url.toLowerCase().includes('=sem')) || hasHsFutureAds || hasModOpenAi;

		// Rule: AVM channel
		if (hasAVMParam) {
			trafficSource = 'avm';
		} else if (hasHBParam) {
			// Rule: HomeBuddy (channel=HB)
			trafficSource = 'hb';
		} else if (hasSemIdentifiers) {
			// Rule: SEM traffic based on gclid, msclkid or =SEM, and absence of CCID/CLK
			trafficSource = 'sem';
		} else if (hasPubIdentifiers && !hasQuadlink && !hasHsFutureAds) {
			// Rule: Publisher or Email traffic
			trafficSource = 'pub';
		}
	}

	return getDetails ? getTrafficSourceDetails(trafficSource, url) : trafficSource;
}
